import {
  resolveSetupGuidePage,
  resolveTourSteps,
  setupGuideRegistry as defaultSetupGuideRegistry,
  tourRegistry,
} from "@bcc/components/tour/registry";
import { TOUR_VERSION } from "@bcc/components/tour/tour-version";
import type { FeatureTour, SetupGuidePage, SetupGuideRegistry, TourStep } from "@bcc/components/tour/tour.types";
import { usePageReady } from "@bcc/components/tour/use-page-ready";
import { useFeatureFlagsContext } from "@bcc/contexts/feature-flags-context";
import { useRBAC } from "@bcc/contexts/rbac-context";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { useRequireInstallation } from "@client-shared/providers/installations";
import { useProductsState } from "@client-shared/providers/products";
import { useVendorContext } from "@client-shared/providers/vendor";
import { findTourCompletion, normalizeTourEmail } from "@shared/schemas/tour-completion.schema";
import type { TourCompletion, TourCompletionUpdate } from "@shared/schemas/tour-completion.schema";
import { FEATURE_FLAG_NAMES } from "@shared/types";
import { setProductSyncRowTourId } from "@bcc/components/tour/tour-ids";
import { tourTargetSelector } from "@bcc/components/tour/tour.utils";
import type React from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

/** Routes where the full tour must never auto-start (auth, onboarding, admin) */
const AUTO_START_BLOCKED_PREFIXES = ["/oauth", "/onboarding", "/admin"];

export type TourStatus = "idle" | "welcome" | "running" | "finished";

/** "full" = every permitted step; "whatsNew" = steps newer than the user's version; "onboarding" = the setup guide */
export type TourMode = "full" | "whatsNew" | "onboarding";

type TourOutcome = "completed" | "skipped";

/** Which way the user last moved through the steps */
export type TourDirection = "forward" | "backward";

export interface TourState {
  status: TourStatus;
  steps: TourStep[];
  currentIndex: number;
  /** High-water mark of `currentIndex`; Back never lowers it, so a later skip still reports the farthest step reached */
  farthestIndexReached: number;
  /** Decides where a step with no available target is skipped to, so Back never bounces forward */
  direction: TourDirection;
  /** True when the tour opened via the first-login auto-start */
  isAutoStarted: boolean;
  /** True when the tour ended via skip rather than completing the last step */
  wasSkipped: boolean;
  mode: TourMode;
  /** Onboarding page whose setup guide is open; only set in onboarding mode */
  setupGuidePage?: SetupGuidePage;
}

export interface StartTourOptions {
  auto?: boolean;
  /** Defaults to "full" */
  mode?: TourMode;
  /** Keep only steps introduced after this registry version (what's-new tours) */
  sinceVersionGreaterThan?: number;
}

export interface TourContextValue {
  state: TourState;
  /** Open the welcome dialog (from the Help menu, or auto on first login) */
  startTour: (options?: StartTourOptions) => void;
  /** Welcome dialog → begin the walkthrough at step 1 */
  beginSteps: () => void;
  /** Advance one step; on the last step marks the tour completed and shows the finish dialog */
  next: () => void;
  prev: () => void;
  /** Leave a step whose target or prerequisite is absent, continuing in the direction the user was travelling */
  skipUnavailableStep: () => void;
  /** End the walkthrough early: marks the tour completed and shows the finish dialog */
  skip: () => void;
  /** Close the finish dialog and return to Home */
  finish: () => void;
  /** Logged-in user's normalized email; undefined for roaster admins impersonating a vendor */
  userEmail?: string;
  /** Tour version this user last completed or skipped; undefined when they never have */
  completedVersion?: number;
  /** Feature-tour version this user last saw or skipped (set by the full tour too); undefined when they never have */
  featureTourVersion?: number;
  /** True when the registry has permitted steps newer than the user's completed version */
  hasPendingUpdates: boolean;
  /** True when the Feature_TourGuide flag (first-login app tour) is on for the vendor's region; false while flags load or on error */
  isTourGuideEnabled: boolean;
  /** True when the Feature_OnboardingTourGuide flag (setup guide auto-start) is on; false while flags load or on error */
  isOnboardingTourGuideEnabled: boolean;
  /** True when the Feature_FeatureTourGuide flag (what's-new auto-start) is on; false while flags load or on error */
  isFeatureTourGuideEnabled: boolean;
  /** Setup guide for the current pathname; undefined off the onboarding pages */
  setupGuidePage?: SetupGuidePage;
  /** True once this user skipped or finished the onboarding setup guide; gates its auto-start */
  isOnboardingTourCompleted: boolean;
  /** True while the full or what's-new walkthrough runs: table views show sample rows instead of the roaster's data */
  isDemoDataActive: boolean;
}

type TourAction =
  | { type: "start"; steps: TourStep[]; auto: boolean; mode: TourMode; setupGuidePage?: SetupGuidePage }
  | { type: "begin" }
  | { type: "next" }
  | { type: "prev" }
  | { type: "skip" }
  | { type: "restart"; toIndex?: number }
  | { type: "close" };

const initialTourState: TourState = {
  status: "idle",
  steps: [],
  currentIndex: 0,
  farthestIndexReached: 0,
  direction: "forward",
  isAutoStarted: false,
  wasSkipped: false,
  mode: "full",
};

/** True while running on the final step, i.e. the next `next` completes the tour */
export const isOnFinalStep = (state: TourState): boolean => state.status === "running" && state.currentIndex >= state.steps.length - 1;

/** The setup guide acts on the roaster's real data, so only the app and what's-new walkthroughs use sample rows */
export const isDemoDataTour = (state: TourState): boolean => state.status === "running" && state.mode !== "onboarding";

/** True while the tour is open and can still be skipped (welcome dialog or walkthrough) */
const isSkippable = (state: TourState): boolean => state.status === "welcome" || state.status === "running";

export const tourReducer = (state: TourState, action: TourAction): TourState => {
  switch (action.type) {
    case "start":
      return {
        status: "welcome",
        steps: action.steps,
        currentIndex: 0,
        farthestIndexReached: 0,
        direction: "forward",
        isAutoStarted: action.auto,
        wasSkipped: false,
        mode: action.mode,
        ...(action.setupGuidePage ? { setupGuidePage: action.setupGuidePage } : {}),
      };
    case "begin":
      return state.status === "welcome"
        ? { ...state, status: "running", currentIndex: 0, farthestIndexReached: 0, direction: "forward" }
        : state;
    case "next": {
      if (state.status !== "running") return state;
      if (isOnFinalStep(state)) return { ...state, status: "finished" };
      const currentIndex = state.currentIndex + 1;
      return { ...state, currentIndex, farthestIndexReached: Math.max(state.farthestIndexReached, currentIndex), direction: "forward" };
    }
    case "prev":
      return state.status === "running" ? { ...state, currentIndex: Math.max(0, state.currentIndex - 1), direction: "backward" } : state;
    case "restart":
      return state.status === "running" ? { ...state, currentIndex: action.toIndex ?? 0, direction: "forward" } : state;
    case "skip":
      return isSkippable(state) ? { ...state, status: "finished", wasSkipped: true } : state;
    case "close":
      return initialTourState;
    default:
      return state;
  }
};

export const TourContext = createContext<TourContextValue | undefined>(undefined);

export interface TourProviderProps {
  readonly children?: React.ReactNode;
  /** Override the tour registry (stories/tests); defaults to the full BCC registry */
  readonly registry?: readonly FeatureTour[];
  /** Override the onboarding setup guides (stories/tests) */
  readonly setupGuideRegistry?: SetupGuideRegistry;
  /** Disable the first-login auto-start (stories/tests) */
  readonly autoStartEnabled?: boolean;
}

const isAutoStartBlockedRoute = (pathname: string): boolean => AUTO_START_BLOCKED_PREFIXES.some((prefix) => pathname.startsWith(prefix));

interface SetupGuideData {
  isInstallationsLoading: boolean;
  hasInstallation: boolean;
  isConnectionsLoading: boolean;
}

/** The caller's whole entry with `changes` applied, so the client cache mirrors what the server stores */
const buildOwnCompletion = (
  ownCompletion: TourCompletion | undefined,
  userEmail: string,
  changes: TourCompletionUpdate
): TourCompletion => ({
  ...ownCompletion,
  userEmail,
  ...changes,
});

/** The setup guide is only relevant while the live data says the page's job is still undone */
const isSetupGuideDataReady = (page: SetupGuidePage | undefined, data: SetupGuideData): boolean => {
  if (page === "connect") return !data.isInstallationsLoading && !data.hasInstallation;
  if (page === "map") {
    return !data.isConnectionsLoading;
  }
  return false;
};

/** The map guide also needs an unmapped row on screen; the SKU-import variant of the page has none */
const buildSetupGuideReadySelector = (page: SetupGuidePage | undefined, registry: SetupGuideRegistry): string | undefined => {
  if (!page) return undefined;
  const { readySelector } = registry[page];
  if (page !== "map") return readySelector;
  return `${readySelector} ${tourTargetSelector(setProductSyncRowTourId("unmapped"))}`;
};

export function TourProvider({
  children,
  registry = tourRegistry,
  setupGuideRegistry = defaultSetupGuideRegistry,
  autoStartEnabled = true,
}: Readonly<TourProviderProps>) {
  const { hasModuleAccess, hasPageAccess, isLoading: isRbacLoading, permissions } = useRBAC();
  const { isFeatureEnabled, isFeatureFlagsLoading } = useFeatureFlagsContext();
  const isTourGuideEnabled = isFeatureEnabled(FEATURE_FLAG_NAMES.tourGuide);
  const isOnboardingTourGuideEnabled = isFeatureEnabled(FEATURE_FLAG_NAMES.onboardingTourGuide);
  const isFeatureTourGuideEnabled = isFeatureEnabled(FEATURE_FLAG_NAMES.featureTourGuide);
  const [vendorState, , vendorUtils] = useVendorContext();
  const { shouldRedirectToOnboarding, isLoading: isInstallationsLoading, hasInstallation } = useRequireInstallation();
  const [productsState] = useProductsState();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(tourReducer, initialTourState);
  const hasAutoStartedRef = useRef(false);
  const autoStartedSetupPagesRef = useRef(new Set<string>());
  const autoStartedWhatsNewRef = useRef(new Set<string>());

  const currentVendor = vendorState.currentVendor;
  const userEmail = currentVendor?.email ? normalizeTourEmail(currentVendor.email) : undefined;
  const ownCompletion = userEmail ? findTourCompletion(currentVendor?.properties?.tourCompletions ?? [], userEmail) : undefined;
  const completedVersion = ownCompletion?.version;
  const featureTourVersion = ownCompletion?.featureTourVersion;
  // The feature tour shows steps newer than what the user has seen: the full tour counts, and nothing recorded means version 1
  const seenFeatureVersion = Math.max(featureTourVersion ?? 0, completedVersion ?? 0, 1);
  const hasSeenCurrentVersion = seenFeatureVersion >= TOUR_VERSION;
  const setupGuidePage = resolveSetupGuidePage(pathname, setupGuideRegistry);
  const setupGuideScope = JSON.stringify([currentVendor?.id, userEmail, setupGuidePage]);
  const isOnboardingTourCompleted = ownCompletion?.onboardingTourCompleted === true;

  const resolveSteps = useCallback(
    (sinceVersionGreaterThan?: number) =>
      resolveTourSteps({ registry, hasModuleAccess, hasPageAccess, isFeatureEnabled, sinceVersionGreaterThan }),
    [registry, hasModuleAccess, hasPageAccess, isFeatureEnabled]
  );

  const resolveSetupGuideSteps = useCallback(
    () => (setupGuidePage ? resolveTourSteps({ registry: [setupGuideRegistry[setupGuidePage].tour], hasModuleAccess, hasPageAccess }) : []),
    [setupGuidePage, setupGuideRegistry, hasModuleAccess, hasPageAccess]
  );

  // True when the user's seen version is behind the registry and permitted steps were introduced since
  const hasPendingUpdates = useMemo(
    () => !hasSeenCurrentVersion && resolveSteps(seenFeatureVersion).length > 0,
    [seenFeatureVersion, hasSeenCurrentVersion, resolveSteps]
  );

  // Fire-and-forget; the vendor record keeps one entry per user and the server merges it
  const writeOwnCompletion = useCallback(
    (changes: TourCompletionUpdate) => {
      if (!userEmail) return;
      // TEMP (f4 testing): don't persist completions so the tour keeps showing; unit tests still exercise the write.
      if (process.env.NODE_ENV !== "test") return;
      const entry = buildOwnCompletion(ownCompletion, userEmail, changes);
      void vendorUtils?.updateProperties({ tourCompletions: [entry] })?.catch(() => undefined);
    },
    [userEmail, ownCompletion, vendorUtils]
  );

  // Main and feature tours: only when a version or the farthest step actually moved.
  // The full tour covers every step, so it records both versions; what's new records only the feature one.
  const markCompleted = useCallback(() => {
    const isFullTour = state.mode === "full";
    const progress = isFullTour ? { stepsCompleted: state.status === "welcome" ? 0 : state.farthestIndexReached + 1 } : {};
    const versionChanged = isFullTour && ownCompletion?.version !== TOUR_VERSION;
    const featureVersionChanged = ownCompletion?.featureTourVersion !== TOUR_VERSION;
    const stepsImproved = progress.stepsCompleted !== undefined && progress.stepsCompleted > (ownCompletion?.stepsCompleted ?? 0);
    if (versionChanged || featureVersionChanged || stepsImproved) {
      writeOwnCompletion({ ...(isFullTour ? { version: TOUR_VERSION } : {}), featureTourVersion: TOUR_VERSION, ...progress });
    }
  }, [state, ownCompletion, writeOwnCompletion]);

  const markOnboardingTourCompleted = useCallback(() => {
    if (!isOnboardingTourCompleted) writeOwnCompletion({ onboardingTourCompleted: true });
  }, [isOnboardingTourCompleted, writeOwnCompletion]);

  // The setup guide is done once skipped anywhere or finished on the map page; finishing the connect page is not the end
  const recordOutcome = useCallback(
    (outcome: TourOutcome) => {
      if (state.mode !== "onboarding") {
        markCompleted();
        return;
      }
      if (outcome === "skipped" || state.setupGuidePage === "map") markOnboardingTourCompleted();
    },
    [state.mode, state.setupGuidePage, markCompleted, markOnboardingTourCompleted]
  );

  const startTour = useCallback(
    (options?: StartTourOptions) => {
      const mode = options?.mode ?? "full";
      if (mode === "onboarding" && (!setupGuidePage || !document.querySelector(setupGuideRegistry[setupGuidePage].readySelector))) return;
      const steps = mode === "onboarding" ? resolveSetupGuideSteps() : resolveSteps(options?.sinceVersionGreaterThan);
      if (!steps.length) return;
      dispatch({ type: "start", steps, auto: options?.auto ?? false, mode, ...(mode === "onboarding" ? { setupGuidePage } : {}) });
    },
    [resolveSteps, resolveSetupGuideSteps, setupGuidePage, setupGuideRegistry]
  );

  const next = useCallback(() => {
    if (isOnFinalStep(state)) recordOutcome("completed");
    dispatch({ type: "next" });
  }, [state, recordOutcome]);

  const skip = useCallback(() => {
    if (isSkippable(state)) recordOutcome("skipped");
    dispatch({ type: "skip" });
  }, [state, recordOutcome]);

  // Going Back into a step that cannot anchor must keep going back; only when
  // there is nothing earlier (or the user was moving forward) does it advance.
  const skipUnavailableStep = useCallback(() => {
    const restartAtStepId = state.steps[state.currentIndex]?.restartAtStepId;
    if (restartAtStepId !== undefined) {
      dispatch({
        type: "restart",
        toIndex: Math.max(
          0,
          state.steps.findIndex((step) => step.id === restartAtStepId)
        ),
      });
      return;
    }
    if (state.direction === "backward" && state.currentIndex > 0) {
      dispatch({ type: "prev" });
      return;
    }
    next();
  }, [state, next]);

  // Closing the finish dialog lands on Home, except for the setup guide which stays on its onboarding page
  const finish = useCallback(() => {
    const goHome = state.mode !== "onboarding" && pathname !== ROUTE_PATHS.HOME;
    dispatch({ type: "close" });
    if (goHome) navigate(ROUTE_PATHS.HOME);
  }, [state.mode, pathname, navigate]);

  // The setup guide closes when the roaster moves on to another page
  useEffect(() => {
    if (state.mode === "onboarding" && state.setupGuidePage && setupGuidePage !== state.setupGuidePage) dispatch({ type: "close" });
  }, [state.mode, state.setupGuidePage, setupGuidePage]);

  // Main tour: needs the user's email (impersonating admins have none), no version for that user, settled flags
  // and RBAC permissions, and no pending onboarding redirect (it lands one render after the pathname check)
  const canAutoStart =
    autoStartEnabled &&
    !isFeatureFlagsLoading &&
    isTourGuideEnabled &&
    !isRbacLoading &&
    permissions !== undefined &&
    currentVendor !== undefined &&
    userEmail !== undefined &&
    completedVersion === undefined &&
    featureTourVersion === undefined &&
    !shouldRedirectToOnboarding &&
    !isAutoStartBlockedRoute(pathname);

  // Hold the welcome dialog until the page has real content (AppLayout done loading, no spinners).
  const isPageReady = usePageReady(canAutoStart);

  useEffect(() => {
    if (!canAutoStart || !isPageReady || hasAutoStartedRef.current) return;
    hasAutoStartedRef.current = true;
    startTour({ auto: true });
  }, [canAutoStart, isPageReady, startTour]);

  // What's new: users behind the current tour version (a missing version counts as 0) get one short tour of the steps added since.
  // The first-login tour takes precedence when it can auto-start; the setup guide is exclusive by route (onboarding routes are blocked).
  const whatsNewScope = JSON.stringify([currentVendor?.id, userEmail]);
  const canAutoStartWhatsNew =
    autoStartEnabled &&
    !isFeatureFlagsLoading &&
    isFeatureTourGuideEnabled &&
    !canAutoStart &&
    !isRbacLoading &&
    permissions !== undefined &&
    currentVendor !== undefined &&
    userEmail !== undefined &&
    hasPendingUpdates &&
    state.status === "idle" &&
    !shouldRedirectToOnboarding &&
    !isAutoStartBlockedRoute(pathname);

  const isWhatsNewPageReady = usePageReady(canAutoStartWhatsNew, { resetKey: whatsNewScope });

  useEffect(() => {
    if (!canAutoStartWhatsNew || !isWhatsNewPageReady) return;
    // Once per user per session, so a failed save cannot reopen it
    if (autoStartedWhatsNewRef.current.has(whatsNewScope)) return;
    autoStartedWhatsNewRef.current.add(whatsNewScope);
    startTour({ auto: true, mode: "whatsNew", sinceVersionGreaterThan: seenFeatureVersion });
  }, [canAutoStartWhatsNew, isWhatsNewPageReady, seenFeatureVersion, whatsNewScope, startTour]);

  // Setup guide: once per onboarding page while its job is undone and the user has not skipped or finished the guide
  const canAutoStartSetupGuide =
    autoStartEnabled &&
    !isFeatureFlagsLoading &&
    isOnboardingTourGuideEnabled &&
    currentVendor !== undefined &&
    userEmail !== undefined &&
    setupGuidePage !== undefined &&
    !isOnboardingTourCompleted &&
    state.status === "idle" &&
    isSetupGuideDataReady(setupGuidePage, {
      isInstallationsLoading,
      hasInstallation,
      isConnectionsLoading: productsState.loading.connections,
    });

  const isSetupGuidePageReady = usePageReady(canAutoStartSetupGuide, {
    resetKey: setupGuideScope,
    readySelector: buildSetupGuideReadySelector(setupGuidePage, setupGuideRegistry),
  });

  useEffect(() => {
    if (!canAutoStartSetupGuide || !isSetupGuidePageReady || !setupGuidePage) return;
    if (autoStartedSetupPagesRef.current.has(setupGuideScope)) return;
    autoStartedSetupPagesRef.current.add(setupGuideScope);
    startTour({ auto: true, mode: "onboarding" });
  }, [canAutoStartSetupGuide, isSetupGuidePageReady, setupGuidePage, setupGuideScope, startTour]);

  const isDemoDataActive = isDemoDataTour(state);

  const value = useMemo<TourContextValue>(
    () => ({
      state,
      startTour,
      beginSteps: () => dispatch({ type: "begin" }),
      next,
      prev: () => dispatch({ type: "prev" }),
      skipUnavailableStep,
      skip,
      finish,
      userEmail,
      completedVersion,
      featureTourVersion,
      hasPendingUpdates,
      isTourGuideEnabled,
      isOnboardingTourGuideEnabled,
      isFeatureTourGuideEnabled,
      setupGuidePage,
      isOnboardingTourCompleted,
      isDemoDataActive,
    }),
    [
      state,
      startTour,
      next,
      skipUnavailableStep,
      skip,
      finish,
      userEmail,
      completedVersion,
      featureTourVersion,
      hasPendingUpdates,
      isTourGuideEnabled,
      isOnboardingTourGuideEnabled,
      isFeatureTourGuideEnabled,
      setupGuidePage,
      isOnboardingTourCompleted,
      isDemoDataActive,
    ]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

/** Tour context, or undefined outside a TourProvider (keeps consumers usable in isolation) */
export function useOptionalTour(): TourContextValue | undefined {
  return useContext(TourContext);
}

export function useTour(): TourContextValue {
  const context = useContext(TourContext);
  if (context === undefined) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
}
